<template>
  <div>
    <v-autocomplete
      class="ma-2"
      clearable
      :items="users"
      label="User"
      v-model="selectedUser"
      placeholder="View user's accessible pages"
      item-text="name"
      item-value="id"
      chips
    />

    <v-select
      label="Roles"
      v-model="filter.roles"
      :items="groupedRoles"
      multiple
      placeholder="Choose roles to filter viewable pages"
      item-text="name"
      item-value="role"
      clearable
      chips
      class="ma-2"
    />

    <v-sheet :elevation="2" class="d-flex">
      <v-treeview
        :active.sync="active"
        :open.sync="open"
        :search="searchTerm"
        :items="filteredPages"
        item-text="label"
        item-key="id"
        return-object
        activatable
        :multiple-active="false"
        selected-color="primary"
        :class="isMobile ? 'flex-grow-1' : ''"
      >
        <template v-slot:prepend="{ item }">
          <v-icon v-if="item.type == 'group'"> mdi-folder-outline </v-icon>
          <v-icon v-if="item.type == 'link'"> mdi-file-link-outline </v-icon>
          <v-icon v-if="item.type == 'seperator'"> mdi-minus </v-icon>
          <v-icon v-if="item.type == 'button'"> mdi-plus </v-icon>
        </template>

        <template slot="label" slot-scope="{ item }">
          <a v-if="item.type == 'button'" @click="addMenu(item)">{{
            item.label
          }}</a>
          <span v-else>
            {{ item.overrideLabel ? item.overrideLabel : item.label }}
          </span>
        </template>
      </v-treeview>
      <v-divider vertical v-if="!isMobile" class="mx-2" />
      <div class="ma-4 flex-grow-1" v-if="!isMobile">
        <div v-if="selectedItem">
          <div
            v-if="selectedItem.type != 'button'"
            class="d-flex justify-space-around ma-2"
          >
            <v-btn v-if="isSuperAdmin" @click="moveUp(selectedItem)">
              move up
            </v-btn>
            <v-btn v-if="isSuperAdmin" @click="moveDown(selectedItem)">
              move down
            </v-btn>
            <v-btn @click="deleteItem(selectedItem)"> delete </v-btn>
          </div>
          <v-divider class="py-2" />
          <v-scroll-y-transition mode="out-in">
            <div v-if="selectedItem.type != 'button'">
              <div class="font-weight-light">
                <label class="mr-6">Sequence:</label>
                <span>{{ selectedItem.sequence }}</span>
              </div>
              <div class="font-weight-light">
                <label class="mr-6">Type:</label>
                <span>{{ selectedItem.type }}</span>
              </div>
              <div v-if="selectedItem.type == 'link' || selectedItem.type == 'cross_module_link'" class="font-weight-light">
                <label class="mr-6">Path:</label>
                <span>{{ selectedItem.endpoint }}</span>
              </div>
              <v-text-field
                v-if="selectedItem.overrideLabel"
                class="mt-4"
                label="Label"
                v-model="selectedItem.overrideLabel"
              />
              <v-text-field
                v-else
                class="mt-4"
                label="Label"
                v-model="selectedItem.label"
              />
              <v-select
                :items="itemRoles"
                v-model="pageRoles"
                item-text="name"
                item-value="role"
                multiple
                clearable
                chips
                label="Choose Permissible Roles"
              />
              <div class="d-flex justify-center">
                <v-btn color="primary" @click="saveItem(selectedItem)">
                  Save
                </v-btn>
              </div>
            </div>
            <div v-if="selectedItem.type == 'button'">
              <div v-if="selectedItem.label == 'Add'">
                Available pages under {{ selectedGroup.label }} group
                <v-list
                  v-if="
                    availableGroupMenuItems &&
                    availableGroupMenuItems.length > 0
                  "
                >
                  <v-list-item
                    v-for="item in availableGroupMenuItems"
                    :key="item.id"
                    @click="addItem(item)"
                  >
                    {{ item.label }}
                  </v-list-item>
                </v-list>
                <div v-else>No option available</div>
              </div>
              <div v-else>
                Available Group:
                <v-list v-if="availableGroups && availableGroups.length > 0">
                  <v-list-item
                    v-for="g in availableGroups"
                    :key="g.id"
                    @click="addMenuGroup(g)"
                  >
                    {{ g.label }}
                  </v-list-item>
                </v-list>
                <div v-else>No group available</div>
              </div>
            </div>
            <div>
              <div>
                {{ selectedItemRoles }}
              </div>
              {{ selectedItem }}
            </div>
          </v-scroll-y-transition>
        </div>
        <div class="text-caption font-weight-light" v-else>
          No item selected
        </div>
      </div>
    </v-sheet>
    <div v-if="isSuperAdmin" class="ma-4">
      <v-divider class="my-2" />

      <v-expansion-panels>
        <v-expansion-panel>
          <v-expansion-panel-header>
            Add New Menu Option (Super Admin Only)
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <page-form />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script>
import {
  getMyAccountPages,
  getAvailablePages,
  getPage2Roles,
  deletePage,
  addPage,
  updateRoles,
  updatePageLabel,
} from "@/services/managePages.js";
import { getAccountModules } from "@/services/module";
import { mapGetters, mapActions } from "vuex";
import { getAccountRoles } from "@/services/accountServices.js";
import intersection from "lodash/intersection";
const PageForm = () => import("@/components/page/PageForm.vue");

export default {
  data: () => ({
    pages: [],
    users: [],
    selectedUser: null,
    allPages: [],
    page2Roles: [],
    filter: {
      roles: [],
    },
    availableRoles: [],
    pageRoles: [],
    availableModules: [],
    active: [],
    open: [],
    ts: Date.now(),
  }),
  watch: {
    //Monitor selected item changed
    active(val) {
      if (val) {
        this.pageRoles = this.selectedItemRoles;
      }
    },
    async selectedUser(val) {
      if (val) {
        //get user's role
        const roles = await this.loadUserRoles(val);
        if (roles) {
          this.filter.roles = roles.map((r) => r.role);
        } else {
          this.filter.roles = [];
        }
      }
    },
  },
  components: {
    PageForm,
  },
  computed: {
    ...mapGetters("auth", ["isLoggedIn", "currentUser", "userRoles"]),

    groupedRoles() {
      if (this.availableRoles) {
        const clone = [...this.availableRoles];
        clone.sort((r1, r2) => (r1.moduleName > r2.moduleName ? 1 : -1));
        const results = [];
        let cursor = null;
        clone.forEach((r) => {
          if (!cursor || cursor != r.moduleName) {
            results.push({ header: r.moduleName });
            cursor = r.moduleName;
          }
          results.push(r);
        });
        return results;
      } else {
        return [];
      }
    },
    filteredPages() {
      console.log(this.ts);
      if (this.pages) {
        const filter = (list) => {
          return list.filter((p) => {
            const pageRoles = this.page2Roles[p.id];
            const matchRoles = intersection(pageRoles, this.filter.roles);
            return this.filter.roles.length == 0 ||
              (matchRoles && matchRoles.length > 0)
              ? true
              : false;
          });
        };

        this.sortPages(this.pages);
        return filter(this.pages);
      } else {
        return [];
      }
    },
    selectedItem() {
      if (this.active && this.active.length > 0) {
        return this.active[0];
      } else {
        return null;
      }
    },
    itemRoles() {
      if (this.active && this.active.length > 0) {
        const item = this.active[0];
        const moduleId = item.moduleId;
        const roles = this.availableRoles.filter(
          (r) => r.moduleId == null || r.moduleId == moduleId
        );
        roles.sort((r1, r2) => (r1.name > r2.name ? 1 : -1));
        return roles;
      } else {
        return null;
      }
    },
    selectedItemRoles() {
      if (this.selectedItem) {
        return this.page2Roles[this.selectedItem.id];
      } else {
        return [];
      }
    },
    selectedGroup() {
      if (this.selectedItem && this.selectedItem.parentId) {
        return this.allPages.find((p) => p.id == this.selectedItem.parentId);
      } else {
        return this.allPages.find((p) => p.id == this.selectedItem.id);
      }
    },
    availableGroupMenuItems() {
      if (this.selectedGroup) {
        const allMenuItems = this.selectedGroup.children;
        const workingGroup = this.pages.find(
          (p) => p.id == this.selectedGroup.id
        );
        const usedMenuItems = workingGroup ? workingGroup.children : [];

        const availableMenuItems = [];

        allMenuItems.forEach((item) => {
          const used = usedMenuItems.find((i) => i.id == item.id);
          if (!used) {
            availableMenuItems.push(item);
          }
        });

        return availableMenuItems;
      } else {
        return [];
      }
    },
    availableGroups() {
      const groups = [];
      this.allPages
        .filter((p1) => p1.type == "menu_group")
        .forEach((p) => {
          const exist = this.pages.find((p1) => p1.id == p.id);
          if (!exist) {
            groups.push(p);
          }
        });

      return groups;
    },
  },
  methods: {
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),
    ...mapActions("user", ["fetchUsers"]),

    appendButtons() {
      this.pages.push({ id: -1, label: "Add Group", type: "button" });
      this.pages.forEach((group, index) => {
        if (group.id > 0) {
          if (!group.children) {
            group.children = [];
          }
          group.children.push({
            id: index * -10,
            label: "Add",
            type: "button",
            parentId: group.id,
          });
        }
      });
    },
    async fetchDependencies() {
      getMyAccountPages().then((resp) => {
        this.pages = resp.data;
        this.sortPages(this.pages);
        this.appendButtons();
        this.loaded = true;
      });

      getAvailablePages().then((resp) => {
        this.allPages = resp.data.content;
        this.sortPages(this.allPages);
      });

      this.page2Roles = await getPage2Roles();

      await getAccountModules().then((resp) => {
        this.availableModules = resp.data.content;
      });

      this.availableRoles = await getAccountRoles();

      if (this.availableModules) {
        this.availableRoles.sort((r1, r2) => (r1.name > r2.name ? -1 : 1));
        this.availableRoles.forEach((r) => {
          const module = this.availableModules.find((m) => m.id == r.moduleId);
          r.moduleName = module ? module.name : "All Modules";
        });
      }

      this.fetchUsers({ pageIndex: 0, size: 1000 }).then((resp) => {
        this.users = resp.content;
        this.users.forEach((u) => (u.name = `${u.firstName} ${u.lastName}`));
      });
    },
    sortPages(pages) {
      if (pages) {
        pages = pages.sort((a, b) => a.sequence - b.sequence);
        pages.forEach((p) => {
          if (p && p.children) {
            this.sortPages(p.children);
          }
        });
      }
    },
    moveUp(item) {
      const list = this.getMoveList(item);
      const opertionItemIndex = list.findIndex((i) => i.id == item.id);
      if (opertionItemIndex > 0) {
        const swapItem = list[opertionItemIndex - 1];
        let temp = swapItem.sequence;
        swapItem.sequence = item.sequence;
        item.sequence = item.sequence == temp ? temp-- : temp;
        this.ts = Date.now();
      }

      this.updateSequence(item);
    },
    moveDown(item) {
      const list = this.getMoveList(item);
      const opertionItemIndex = list.findIndex((i) => i.id == item.id);
      if (opertionItemIndex >= 0 && opertionItemIndex < list.length - 2) {
        const swapItem = list[opertionItemIndex + 1];
        let temp = swapItem.sequence;
        swapItem.sequence = item.sequence;
        item.sequence = item.sequence == temp ? temp++ : temp;
        this.ts = Date.now();
      }

      this.updateSequence(item);
    },
    getMoveList(item) {
      let list = [];
      if (item.type == "menu_group") {
        list = this.filteredPages;
      } else {
        list = this.filteredPages.find((p) => p.id == item.parentId).children;
      }
      return list;
    },
    updateSequence(item) {
      let list = [];
      if (item.parentId) {
        list = this.pages.find((p) => p.id == item.parentId).children;
      } else {
        list = this.pages;
      }

      this.sortPages(list);

      const idsInSequence = list.filter((p) => p.id > 0).map((p) => p.id);
      // const nameInSequence = list.filter(p => p.id > 0).map(p => `${p.label} - ${p.sequence}`);
      this.$axios.post("/page/update-sequence", {
        pageIdsInSequence: idsInSequence,
      });
      // console.log('saving sequences: ', nameInSequence)
    },
    deleteItem(item) {
      if (item.parentId) {
        const group = this.pages.find((p) => p.id == item.parentId);
        if (group) {
          const index = group.children.findIndex((l) => l.id == item.id);
          if (group) {
            deletePage(item.id).then(() => {
              this.addSuccessMessage("Deleted menu item");
              group.children.splice(index, 1);
            });
          }
        }
      } else {
        const index = this.pages.findIndex((p) => p.id == item.id);
        if (index > -1) {
          deletePage(item.id).then(() => {
            this.addSuccessMessage("Deleted menu group");
            this.pages.splice(index, 1);
          });
        }
      }

      this.active = [];
    },
    addItem(item) {
      const selectedGroup = this.allPages.find((p) => p.id == item.parentId);
      const sequence = Math.max(
        selectedGroup.children.map((item) => item.sequence)
      );
      addPage(item.id, sequence)
        .then(() => {
          this.addSuccessMessage("Menu item added");
          const group = this.pages.find((p) => p.id == selectedGroup.id);
          group.children.splice(group.children.length - 1, 0, item);
          this.active = [item];
        })
        .catch((error) => {
          this.addErrorMessage(error.data.message);
        });
    },
    addMenuGroup(group) {
      const sequence = null;
      addPage(group.id, sequence)
        .then(() => {
          this.addSuccessMessage("Menu group added");
          group.children = [
            {
              id: group.id * -10,
              label: "Add",
              type: "button",
              parentId: group.id,
            },
          ];
          this.pages.splice(this.pages.length - 1, 0, group);
          this.active = [group];
        })
        .catch((error) => {
          this.addErrorMessage(error.data.message);
        });
    },
    saveItem(item) {
      console.log(item);
      const roles = this.pageRoles;

      updatePageLabel(item.id, item.label).then(() => {
        updateRoles(item.id, roles).then(() => {
          this.page2Roles[item.id] = this.pageRoles;
          this.addSuccessMessage("Permission updated");
        });
      });
    },
    addMenu() {
      console.log(this.selectedItem);
    },
    isSuperAdmin() {
      if (this.userRoles && this.userRoles.length > 0) {
        return this.userRoles.includes("SUPER");
      } else {
        return false;
      }
    },
    async loadUserRoles(userId) {
      return await this.$axios("/user/roles", {
        params: { user_id: userId },
      }).then((resp) => {
        return resp.data;
      });
    },
  },
  created() {
    this.fetchDependencies();
  },
};
</script>

<style>
</style>